import { useTranslation } from "react-i18next";
import { apiRequest } from "@/helper/api";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import type { NotificationVariant } from "@/components/tables/hooks/useTableNotifications";
import type { EntityAdapter } from "@/components/tables/entityAdapters/entityAdapter";

export interface UseRowDeleteOptions {
  entity: string;
  entityAdapter?: EntityAdapter;
  companySubjectId?: string;
  refreshTableAfterAction?: () => void;
  notify: (message: any, variant: NotificationVariant) => void;
  requestReload: () => void;
  onError: (error: any) => void;
}

interface DeletableRow {
  toggleSelected: (selected: boolean) => void;
  getValue: (columnId: string) => any;
}

export function useRowDelete(options: UseRowDeleteOptions) {
  const { t } = useTranslation();
  const { showConfirmationModalAsync } = useConfirmationModal();

  const deleteRow = async (row: DeletableRow, skipQuestion = false) => {
    if (!skipQuestion) {
      const isConfirmed = await showConfirmationModalAsync(
        t(options.entityAdapter?.deleteQuestionKey ?? "delete_entry_question"),
        { confirmButtonText: t("delete") }
      );
      if (!isConfirmed) {
        return;
      }
    }
    row.toggleSelected(false);

    try {
      await apiRequest({
        method: "delete",
        entity: options.entity,
        entityId: row.getValue("id"),
        parametersToRender: {
          additionalUrlParameters: options.companySubjectId
            ? { impersonate_subject: options.companySubjectId }
            : {},
          depth: "0",
        },
        additionalRouteParts: {},
      });
      options.notify(t("delete_successful"), "success");
      options.requestReload();
      options.refreshTableAfterAction?.();
    } catch (error) {
      options.onError(error);
    }
  };

  return { deleteRow };
}
